import React from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";

import DateTimePicker from "@/src/components/common/tools/DateTimePicker";
import { CheckboxFieldBase } from "@/src/components/form-fields/CheckboxField";
import { SwitchFieldBase } from "@/src/components/form-fields/SwitchField";
import { Label } from "@/src/components/ui/label";

import type { JSX } from "react";

export interface RestrictionPeriodChange {
  checked: boolean;
  beginDate: string;
  endDate: string | null;
}

export interface TemporalRestrictionFields {
  temporal_restriction: boolean;
  begin_date: string;
  end_date: string | null;
}

export function toTemporalRestrictionFields({
  checked,
  beginDate,
  endDate,
}: RestrictionPeriodChange): TemporalRestrictionFields {
  return {
    temporal_restriction: checked,
    begin_date: beginDate,
    end_date: endDate,
  };
}

interface RestrictionPeriodFieldBaseProps {
  label: string;
  checked: boolean;
  beginDate?: string | null;
  endDate?: string | null;
  disabled?: boolean;
  enablePastStartDate?: boolean;
  lang?: string;
  popoverContainer?: HTMLElement | null;
  onChange: (change: RestrictionPeriodChange) => void;
}

function toApiDate(date: Date | string): string {
  return moment.utc(date).format();
}

export function RestrictionPeriodFieldBase({
  label,
  checked,
  beginDate,
  endDate,
  disabled,
  enablePastStartDate,
  lang,
  popoverContainer,
  onChange,
}: RestrictionPeriodFieldBaseProps): JSX.Element {
  const { t } = useTranslation("editor-smartlist");
  const currentBeginDate = beginDate?.length
    ? beginDate
    : toApiDate(new Date());
  const currentEndDate = endDate ?? null;

  const emit = (change: Partial<RestrictionPeriodChange>): void => {
    onChange({
      checked,
      beginDate: currentBeginDate,
      endDate: currentEndDate,
      ...change,
    });
  };

  const handleCheckedChange = (nextChecked: boolean): void => {
    // Turning off resets the window: every caller relies on this legacy contract.
    if (!nextChecked) {
      emit({ checked: false, beginDate: toApiDate(new Date()), endDate: null });
      return;
    }
    emit({ checked: true });
  };

  const handleEndDateToggle = (isEndDateChecked: boolean): void => {
    emit({
      endDate: isEndDateChecked
        ? toApiDate(moment.utc(currentBeginDate).add(1, "day").toDate())
        : null,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <SwitchFieldBase
        checked={checked}
        disabled={disabled}
        label={label}
        onCheckedChange={handleCheckedChange}
        variant="inline"
      />
      {checked ? (
        // pl-11 = switch w-8 + Field gap-3, aligns with the label text
        <div className="flex flex-wrap gap-4 pl-11">
          <div className="flex flex-col gap-2">
            <Label className="leading-snug">{t("startDate")}</Label>
            <DateTimePicker
              disabled={disabled}
              lang={lang}
              maxDate={currentEndDate ? new Date(currentEndDate) : undefined}
              minDate={enablePastStartDate ? undefined : new Date()}
              onDateTimeChange={(date) => emit({ beginDate: toApiDate(date) })}
              popoverContainer={popoverContainer}
              value={new Date(currentBeginDate)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <CheckboxFieldBase
              checked={Boolean(currentEndDate)}
              disabled={disabled}
              label={t("endDate")}
              onCheckedChange={handleEndDateToggle}
            />
            <DateTimePicker
              disabled={Boolean(disabled) || !currentEndDate}
              lang={lang}
              minDate={new Date(currentBeginDate)}
              onDateTimeChange={(date) => emit({ endDate: toApiDate(date) })}
              popoverContainer={popoverContainer}
              value={currentEndDate ? new Date(currentEndDate) : undefined}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
